import { useSyncExternalStore } from "react";

interface ClockStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => number;
}

const clockStores = new Map<number, ClockStore>();

export function useNow(intervalMs = 15_000): number {
  const store = getClockStore(intervalMs);
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

function getClockStore(intervalMs: number): ClockStore {
  const existing = clockStores.get(intervalMs);
  if (existing) return existing;

  let now = Date.now();
  let timerId: number | null = null;
  const listeners = new Set<() => void>();

  const emit = () => {
    now = Date.now();
    listeners.forEach((listener) => listener());
  };

  const handleVisibilityChange = () => {
    if (!document.hidden) emit();
  };

  const start = () => {
    if (timerId !== null) return;
    timerId = window.setInterval(emit, intervalMs);
    document.addEventListener("visibilitychange", handleVisibilityChange);
  };

  const stop = () => {
    if (timerId === null) return;
    window.clearInterval(timerId);
    timerId = null;
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };

  const store: ClockStore = {
    subscribe(listener) {
      listeners.add(listener);
      if (listeners.size === 1) start();
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) stop();
      };
    },
    getSnapshot() {
      return now;
    },
  };

  clockStores.set(intervalMs, store);
  return store;
}
